'use client';

import { useCallback, useEffect, useRef } from 'react';

import {
	safeInterviewRedirect,
	useCandidateAnalytics,
} from '@/features/candidate-interview';
import { Button } from '@/shared';
import { Dialog } from '@/shared/ui/dialog';

export function CandidatePromoOutcome({
	interviewId,
	url,
	outcome,
}: {
	interviewId: string;
	url?: string;
	outcome: 'ready' | 'credits_exhausted' | 'report_credits_exhausted';
}) {
	const track = useCandidateAnalytics(interviewId, 'complete');
	const seen = useRef(false);
	const navigating = useRef(false);
	const target = safeInterviewRedirect(url);
	const navigate = useCallback(
		(action: 'automatic' | 'button' | 'close') => {
			if (navigating.current) return;
			if (
				!target ||
				new URL(target, window.location.origin).href === window.location.href
			) {
				track('promo_sales_redirect_error', {
					action,
					outcome: 'invalid_redirect',
				});
				return;
			}
			navigating.current = true;
			track('promo_sales_redirect', { action, outcome });
			window.location.replace(target);
		},
		[target, track, outcome],
	);
	useEffect(() => {
		if (!seen.current) {
			seen.current = true;
			track('promo_outcome_view', { outcome });
		}
		if (outcome === 'credits_exhausted' || !target) return;
		const timer = setTimeout(
			() => navigate('automatic'),
			outcome === 'ready' ? 5000 : 0,
		);
		return () => clearTimeout(timer);
	}, [outcome, target, navigate, track]);
	return (
		<Dialog
			open
			onClose={() => navigate('close')}
			title={
				outcome === 'ready' ? 'Интервью завершено' : 'Недостаточно кредитов'
			}
			footer={
				<div className="px-4 pb-8 pt-2 sm:px-10">
					<Button block disabled={!target} onClick={() => navigate('button')}>
						Продолжить обучение
					</Button>
				</div>
			}
		>
			<div className="space-y-3 px-4 py-6 text-sm text-gray-3 sm:px-10">
				{outcome === 'ready' ? (
					<>
						<p>Отчёт готов. Отправляем его на вашу почту.</p>
						<p>
							Через 5 секунд откроется страница, где можно продолжить обучение.
						</p>
					</>
				) : outcome === 'report_credits_exhausted' ? (
					<p>
						Для подготовки отчёта не хватило кредитов. Уведомление отправим на
						email. Переходим на страницу обучения.
					</p>
				) : (
					<p>
						Кредиты для работы с AI закончились, поэтому интервью остановлено.
						Перейдите на страницу обучения, чтобы выбрать подходящий тариф.
					</p>
				)}
				{!target && (
					<p role="alert">
						Ссылка на обучение пока не настроена. Обратитесь в поддержку.
					</p>
				)}
			</div>
		</Dialog>
	);
}
